import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, X, Trash2, RefreshCw, ArrowLeft, MailWarning, Inbox } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function ErrorLog() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.group === "admin";
  const [errors, setErrors] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const localeForDate = i18n.language === "zh" ? "zh-CN" : i18n.language === "ms" ? "ms-MY" : "en-GB";

  const load = () => {
    setLoading(true);
    const calls = [api.get("/errors")];
    if (isAdmin) calls.push(api.get("/email-events", { params: { problems_only: true, limit: 200 } }));
    Promise.all(calls)
      .then((res) => {
        setErrors(res[0].data);
        if (isAdmin && res[1]) setEvents(res[1].data);
      })
      .catch(() => toast.error(t("errors.loadFailed")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissOne = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/errors/${id}`);
      setErrors((prev) => prev.filter((e) => e.id !== id));
      toast.success(t("errors.dismissed"));
    } catch (e) {
      toast.error(t("errors.dismissFailed"));
    } finally {
      setBusyId(null);
    }
  };

  const dismissEvent = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/email-events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast.success(t("errors.eventDismissed"));
    } catch (e) {
      toast.error(t("errors.dismissFailed"));
    } finally {
      setBusyId(null);
    }
  };

  const dismissAll = async () => {
    try {
      const r1 = await api.delete("/errors");
      let evCount = 0;
      if (isAdmin) {
        const r2 = await api.delete("/email-events");
        evCount = r2.data?.dismissed ?? 0;
      }
      setErrors([]);
      setEvents([]);
      setConfirmClear(false);
      toast.success(t("errors.summaryToast", { errors: r1.data.dismissed ?? 0, events: evCount }));
    } catch (e) {
      toast.error(t("errors.dismissFailed"));
    }
  };

  const totalCount = errors.length + events.length;
  const eventLabel = (status) =>
    t(`errors.labels.${status}`, { defaultValue: status });

  return (
    <div data-testid="error-log-page">
      <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
            {t("errors.breadcrumb")}
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="text-amber-500" size={28} />
            {t("errors.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-3xl">
            {t("errors.description")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/dashboard"
            className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 flex items-center gap-2"
            data-testid="error-back-btn"
          >
            <ArrowLeft size={14} />
            {t("errors.dashboard")}
          </Link>
          <button
            onClick={load}
            className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 flex items-center gap-2"
            data-testid="error-refresh-btn"
          >
            <RefreshCw size={14} />
            {t("errors.refresh")}
          </button>
          {isAdmin && totalCount > 0 && (
            <button
              onClick={() => setConfirmClear(true)}
              className="px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold flex items-center gap-2"
              data-testid="error-clear-all-btn"
            >
              <Trash2 size={14} />
              {t("errors.clearAll")}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="pg-card p-12 text-center text-slate-400" data-testid="error-loading">
          {t("errors.loading")}
        </div>
      ) : (
        <>
          {/* File errors */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={16} className="text-rose-500" />
              <h2 className="text-base font-bold text-slate-800">
                {t("errors.fileErrors")} <span className="text-slate-400 font-medium">({errors.length})</span>
              </h2>
            </div>
            {errors.length === 0 ? (
              <div className="pg-card p-8 text-center dot-grid" data-testid="error-empty">
                <AlertTriangle className="mx-auto text-emerald-400 mb-2" size={36} />
                <p className="text-slate-600 font-semibold">{t("errors.noFileErrors")}</p>
                <p className="text-slate-400 text-sm">{t("errors.allHealthy")}</p>
              </div>
            ) : (
              <div className="pg-card overflow-x-auto" data-testid="error-table">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                      <th className="px-4 py-3">{t("errors.columns.date")}</th>
                      <th className="px-4 py-3">{t("errors.columns.store")}</th>
                      <th className="px-4 py-3">{t("errors.columns.originalFile")}</th>
                      <th className="px-4 py-3">{t("errors.columns.source")}</th>
                      <th className="px-4 py-3">{t("errors.columns.error")}</th>
                      {isAdmin && <th className="px-4 py-3 w-24 text-right">{t("errors.columns.action")}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((e) => (
                      <tr
                        key={e.id}
                        className="border-b border-slate-100 hover:bg-slate-50/60"
                        data-testid={`error-row-${e.id}`}
                      >
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {new Date(e.uploaded_at).toLocaleString(localeForDate, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/stores/${e.store_id}/files`}
                            className="font-semibold text-blue-700 hover:text-cyan-600"
                          >
                            {e.store_name}
                          </Link>
                          {e.machine_id && (
                            <div className="text-[11px] text-slate-400 uppercase tracking-wider">
                              MID · {e.machine_id}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700 break-all max-w-xs">
                          {e.original_filename}
                        </td>
                        <td className="px-4 py-3">
                          <span className="pill pill-info uppercase text-[10px]">
                            {t(`common.sources.${e.source}`, { defaultValue: e.source })}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-rose-600 break-all max-w-md">
                          {e.error_message || t("errors.unknownError")}
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => dismissOne(e.id)}
                              disabled={busyId === e.id}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-rose-50 hover:border-rose-200 text-rose-600 text-xs font-semibold disabled:opacity-50"
                              data-testid={`error-dismiss-${e.id}`}
                            >
                              <X size={12} />
                              {t("common.dismiss")}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Email events */}
          {isAdmin && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <MailWarning size={16} className="text-amber-500" />
                <h2 className="text-base font-bold text-slate-800">
                  {t("errors.orphanEmails")}{" "}
                  <span className="text-slate-400 font-medium">({events.length})</span>
                </h2>
                <span className="text-xs text-slate-400 ml-2">
                  {t("errors.orphanSubtitle")}
                </span>
              </div>
              {events.length === 0 ? (
                <div className="pg-card p-8 text-center dot-grid" data-testid="events-empty">
                  <Inbox className="mx-auto text-emerald-400 mb-2" size={36} />
                  <p className="text-slate-600 font-semibold">{t("errors.noOrphan")}</p>
                  <p className="text-slate-400 text-sm">
                    {t("errors.everyRouted")}
                  </p>
                </div>
              ) : (
                <div className="pg-card overflow-x-auto" data-testid="events-table">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                        <th className="px-4 py-3">{t("errors.columns.date")}</th>
                        <th className="px-4 py-3">{t("errors.columns.reason")}</th>
                        <th className="px-4 py-3">{t("errors.columns.fromTo")}</th>
                        <th className="px-4 py-3">{t("errors.columns.subject")}</th>
                        <th className="px-4 py-3">{t("errors.columns.midAttachment")}</th>
                        <th className="px-4 py-3">{t("errors.columns.detail")}</th>
                        <th className="px-4 py-3 w-24 text-right">{t("errors.columns.action")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((ev) => (
                        <tr
                          key={ev.id}
                          className="border-b border-slate-100 hover:bg-slate-50/60"
                          data-testid={`event-row-${ev.id}`}
                        >
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                            {new Date(ev.created_at).toLocaleString(localeForDate, {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                          <td className="px-4 py-3">
                            <span className="pill pill-warning uppercase text-[10px]">
                              {eventLabel(ev.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[12px] text-slate-600 break-all max-w-[220px]">
                            <div className="font-semibold text-slate-700 truncate" title={ev.from_address}>
                              {ev.from_address || "—"}
                            </div>
                            <div className="text-slate-400 truncate" title={ev.to_address}>
                              → {ev.to_address || "—"}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-700 break-all max-w-[220px]">
                            {ev.subject || "—"}
                          </td>
                          <td className="px-4 py-3 text-[12px] mono text-slate-600">
                            {ev.machine_id ? (
                              <div>MID · {ev.machine_id}</div>
                            ) : (
                              <div className="text-slate-300">{t("errors.noMid")}</div>
                            )}
                            {ev.attachment_name && (
                              <div className="text-slate-500 truncate max-w-[180px]" title={ev.attachment_name}>
                                {ev.attachment_name}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-rose-600 break-all max-w-[260px]">
                            {ev.message || "—"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => dismissEvent(ev.id)}
                              disabled={busyId === ev.id}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-rose-50 hover:border-rose-200 text-rose-600 text-xs font-semibold disabled:opacity-50"
                              data-testid={`event-dismiss-${ev.id}`}
                            >
                              <X size={12} />
                              {t("common.dismiss")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {confirmClear && (
        <div
          className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4"
          onClick={() => setConfirmClear(false)}
          data-testid="error-confirm-modal"
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-slate-900 mb-2">{t("errors.clearTitle")}</h3>
            <p className="text-sm text-slate-600 mb-5">
              {t("errors.clearDescription", { errors: errors.length, events: events.length })}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmClear(false)}
                className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"
                data-testid="error-confirm-cancel"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={dismissAll}
                className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold"
                data-testid="error-confirm-clear"
              >
                {t("errors.clearYes")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
